<template>
  <div class="iiif-viewer" :class="{ 'is-full': full }">
    <div class="viewer-container" v-resize="resize">
      <div class="viewer" ref="leaflet" />

      <div class="iiif-viewer-blocker" @click="removeBlock" @touch="removeBlock" v-show="block" />

      <div class="iiif-control-slider" v-if="totalPage > 1 && showPage">
        <BaseSlider
          @update:model-value="inputPage(250)"
          v-model.number="inputPageModel"
          :min="1"
          :max="totalPage"
        />
      </div>

      <div class="iiif-control-group">
        <div class="control-primary">
          <template v-if="totalPage && showPage">
            <div class="iiif-control-page">
              <UiButtonIcon
                tag="button"
                class="jps-iiif-list jps-icon-button"
                icon="mdi/view-grid"
                :arialabel="lss('一覧', 'List')"
                @click="showList()"
              />
              <input
                class="jps-iiif-inputpage is-size-6"
                @input="inputPage(1000)"
                v-model.number="inputPageModel"
                :key="inputPageModeRefresh"
              />
              <span class="iiif-total is-static">{{ "/" + totalPage }}</span>
            </div>
            <div class="iiif-annot">
              <UiButtonIcon
                tag="button"
                class="jps-iiif-prev jps-icon-button"
                icon="mdi/arrow-left-thick"
                :arialabel="lss('前へ', 'Previous')"
                @click="previous()"
                :disabled="totalPage === 1"
              />
              <UiButtonIcon
                tag="button"
                class="jps-iiif-next jps-icon-button"
                icon="mdi/arrow-right-thick"
                :arialabel="lss('次へ', 'Next')"
                @click="next()"
                :disabled="totalPage === 1"
              />
            </div>
            <div class="iiif-control-separator" />
            <ThumbnailListModal ref="thumbnailListModal" />
          </template>

          <div class="iiif-annot" v-if="mode === 'edit-rect' || annots">
            <UiButtonIcon
              v-if="mode === 'edit-rect'"
              tag="button"
              class="label-button"
              icon="mdi/shape-rectangle-plus"
              @click="addRect()"
              arialabel="rect"
            />

            <span class="jps-select" v-if="annots">
              <select v-model="selectedIndex">
                <option :value="-1"></option>
                <option v-for="(a, ind) in annots" :key="ind" :value="ind">
                  {{ ls(a) }}
                </option>
              </select>
            </span>
            <div class="iiif-control-separator" />
          </div>

          <div class="iiif-control-zoom">
            <template v-if="!noRotation">
              <UiButtonIcon
                tag="button"
                class="jps-iiif-rotate label-button"
                icon="mdi/rotate-right"
                @click="rotate()"
                :arialabel="lss('回転', 'Rotate')"
              />
              <div class="iiif-control-separator" />
            </template>

            <UiButtonIcon
              tag="button"
              class="jps-iiif-fit label-button"
              icon="mdi/plus"
              @click="map.zoomIn()"
              :arialabel="lss('拡大', 'Zoom up')"
            />

            <UiButtonIcon
              tag="button"
              class="jps-iiif-fit label-button"
              icon="mdi/arrow-expand-all"
              @click="fitBounds()"
              :arialabel="lss('リセット', 'Reset')"
            />

            <UiButtonIcon
              tag="button"
              class="jps-iiif-fit label-button"
              icon="mdi/minus"
              @click="map.zoomOut()"
              :arialabel="lss('縮小', 'Zoom down')"
            />

            <template v-if="mode !== 'edit-rect'">
              <div class="iiif-control-separator" />
              <UiButtonIcon
                tag="button"
                class="jps-iiif-full label-button"
                :icon="full ? 'mdi/fullscreen-exit' : 'mdi/fullscreen'"
                @click="changeFull()"
                :arialabel="ls(full
                  ? { ja: 'フルスクリーン解除', en: 'Quit full Screen'}
                  : { ja: 'フルスクリーン', en: 'Full Screen'}
                )"
              />
            </template>
          </div>
        </div>

        <div class="control-secondery">
          <slot name="info" />

          <div
            v-if="image.type === 'MANIFEST' && !noIiifLogo"
            class="jps-iiif-api is-hidden-mobile"
          >
            <a :href="image.url" class="jps-iiif-manifest">
              <BaseIcon name="icon/iiif-logo" alt="IIIF" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import vResize from '~/directives/resize-observer'
import type { ThumbnailListModal } from '#components'

const { lss, ls } = useI18n()
const { iiifImageUrlFromInfo } = useIIIF()
const { fetchGetWithoutCustomHeader } = useCustomFetch()
const {
  $createLeafletMap,
  $createLeafletLayerGroup,
  $createLeafletLatLngBounds,
  $createLeafletUrlImage,
  $getLeafletCrsSimple,
  $createLeafletIIIFLayer,
  $createLeafletRectangle,
} = useNuxtApp()

interface Props {
  noIiifLogo?: boolean
  noRotation?: boolean
  image: ViewerInfo
  rects?: Rect[]
  annots?: LangString[]
  ratio?: number
  mode?: 'edit-rect' | 'show-rect' | 'none' | ''
}
const props = withDefaults(defineProps<Props>(), {
  noIiifLogo: false,
  noRotation: false,
})

const emits = defineEmits(['update-rects', 'select-rect', 'url'])

const full: Ref<boolean> = ref(false)
const imageLoaded = ref(false)
const rotation: Ref<number> = ref(0)

const leaflet = ref<HTMLElement>(null)

//leaflet要素：vueの管理外
let map: any // L.Map
let rectLayer: any // L.LayerGroup
let imageLayer: any // L.LayerGroup

onMounted(async () => {

  rectLayer = $createLeafletLayerGroup()
  const leafletParam = {
    attributionControl: false,
    zoomControl: false,
    wheelDebounceTime: 200,
    wheelPxPerZoomLevel: 60,
    zoomSnap: 0.1,
    //center: <any>[0, 0],
    center: [0, 0] as any,
    crs: $getLeafletCrsSimple(),
    zoom: 0,
    editable: true,
    editOptions: { featuresLayer: rectLayer },
  }

  map = $createLeafletMap(leaflet.value, leafletParam)
  rectLayer.addTo(map)
  imageLayer = $createLeafletLayerGroup()
  imageLayer.addTo(map)

  //EscでFull Screenをやめる
  document.addEventListener('keydown', escapeFull)
  watchImage(props.image, null)
  initRect()
})

const block: Ref<boolean> = ref(true)
const removeBlock = () => {
  block.value = false
}

onBeforeUnmount(() => {
  try {
    map.remove()
  } catch (e) {}
  document.removeEventListener('keydown', escapeFull)
  closeModalWithHistory(null, closeFull)
})

onUnmounted(() => {
  if (full.value) changeFull()
})

const escapeFull = e => {
  if (e.key === 'Escape') {
    if (full.value) changeFull()
  }
}

let isFirstResize = true
const resize = () => {
  nextTick(() => {
    if (imageLoaded.value) {
      if (isFirstResize) {
        isFirstResize = false
        // 新しいタブで開いた時に画像が小さく表示される場合があるため一回待ってからリサイズする
        setTimeout(() => {
          resize()
        }, 500)
      } else {
        map.invalidateSize()
        fitBounds()
      }
    } else {
      setTimeout(() => {
        resize()
      }, 500)
    }
  })
}

const showPage = computed(() => {
  return props.mode !== 'show-rect'
})

const watchImage = (neu: ViewerInfo, old) => {
  if (neu) {
    if (old
        && neu.type === old.type
        && neu.url === old.url
        && neu.urls === old.urls
        && neu.firstPage === old.firstPage) {
      // 実際に過去の値と変わってない場合は処理しない
      return
    }
    if (neu.type === 'INFO' && neu.url) {
      setInfo(neu.url)
    } else if (neu.type === 'MANIFEST' && neu.url) {
      setManifest(neu.url, neu.firstPage)
    } else if (neu.type === 'DIRECT') {
      if (neu.url) {
        setUrl(neu.url)
      } else if (neu.urls) {
        setUrls(neu.urls)
      }
    }
  }
}

watch(
  () => props.image,
  watchImage,
  { deep: true }
)

const rotate = () => {
  rotation.value += 90
  if (rotation.value === 360) rotation.value = 0
  if (infoUrl.value) {
    setInfo(infoUrl.value)
  } else if (url.value) {
    setUrl(url.value)
  }
}

const changeFull = () => {
  removeBlock()
  full.value = !full.value
  if (full.value) {
    showModalWithHistory('full-viewer', closeFull)
  } else {
    closeModalWithHistory('full-viewer', closeFull)
  }
  nextTick(() => {
    map.invalidateSize()
    fitBounds()
  })
}

const closeFull = () => {
  full.value = false
  nextTick(() => {
    map.invalidateSize()
    fitBounds()
  })
}

const fitBounds = () => {
  if (iiifLayer) {
    iiifLayer._fitBounds()
  } else {
    map.fitBounds(bounds, { animate: true })
  }
}

const clearLayers = () => {
  try {
    imageLayer.clearLayers()
    iiifLayer = null
    urlImage = null
    infoUrl.value = null
    url.value = null
  } catch (e) {
    //parent node undefinedのエラーが出るが、無視
  }
  map.invalidateSize()
}

/////////////URL指定////////////////

let bounds: any // L.LatLngBounds
let urlImage: any // L.ImageOverlay
const urls: Ref<string[]> = ref([])
const url: Ref<string> = ref(null)

const setUrls = (_urls: string[]) => {
  urls.value = _urls
  setUrl(_urls[0])
}

const setUrl = (src: string) => {
  // console.info('set url', src)
  clearLayers()
  map.setMaxZoom(6)
  map.setMinZoom(1)
  const img: HTMLImageElement = new Image()
  imageLoaded.value = false
  img.onload = () => {
    window.setTimeout(() => {
      if (url.value != src) {
        clearLayers()
        //原因不明だが、この無名関数は2回以上動くことがあるため、urlImageがnullで無いなら動作しないようにしている。
        let southWest = map.unproject([0, img.height], map.getMaxZoom() - 1)
        let northEast = map.unproject([img.width, 0], map.getMaxZoom() - 1)
        bounds = $createLeafletLatLngBounds(southWest, northEast)
        urlImage = $createLeafletUrlImage(src, bounds, { rotation: rotation.value }, imageLayer)
        map.fitBounds(bounds, { animate: false })
        imageLoaded.value = true
        initRect()
        url.value = src
      }
    }, 250)
  }
  img.src = src
  emits('url', src)
}

//////////////////IIIF///////////////

let iiifLayer: any

///Info
const infoUrl = ref('')

const setInfo = (_infoUrl: string) => {
  clearLayers()
  imageLoaded.value = false
  //iiifLayer = (<any>L.GridLayer).iiifRotate(_infoUrl, {
  iiifLayer = $createLeafletIIIFLayer(_infoUrl, { rotate: rotation.value })
  iiifLayer.on('iiif-load-json', e => {
    initRect()
    imageLoaded.value = true
  })
  imageLayer.addLayer(iiifLayer)

  infoUrl.value = _infoUrl
  emits('url', _infoUrl)
}

let iiifType = ref<'normal' | 'direct' | null>(null)
const isV3 = ref(false)

///Manifest
const manifest: Ref<any> = ref(null)
const currentPage: Ref<number> = ref(null)
const setManifest = async (manifestUrl: string, infoJson?: string) => {
  const raw = await fetchGetWithoutCustomHeader(manifestUrl);
  let _manifest: any = null;
  if (raw != null) {
    if (typeof raw === 'string') {
      try {
        _manifest = JSON.parse(raw);
      } catch { 
        _manifest = null;
      }
    } else if (typeof raw === 'object') {
      _manifest = raw as Record<string, any>;
    }
  }
  if (!_manifest) {
    return
  }
  manifest.value = _manifest
  try {
    if (
      manifest.value['@context'] ===
      'http://iiif.io/api/presentation/3/context.json'
    ) {
      isV3.value = true
      /*if (manifest.value.items[0].items[0].items[0].body.service) {
        iiifType.value = 'normal'
      } else {
        iiifType.value = 'direct'
      }*/
      iiifType.value = 'direct'
    } else {
      if (
        manifest.value.sequences[0].canvases[0].images[0].resource.service
      ) {
        iiifType.value = 'normal'
      } else {
        iiifType.value = 'direct'
      }
    }
  } catch {
    try {
      if (manifest.value.items[0].items[0].items[0].body.service[0].type) {
        iiifType.value = 'normal'
        isV3.value = true
      }
    } catch {
      iiifType.value = 'direct'
    }
  }
  if (iiifType.value === 'normal' && infoJson) {
    try {
      infoJson = infoJson.replace(/\/info\.json$/, '')
      if (isV3.value) {
        if (manifest.value.items[0].forEach) {
          manifest.value.items[0].forEach((item, i) => {
            if (item.items[0].items[0].body.service['@id'] === infoJson){
              currentPage.value = i + 1
            } else if (item.items[0].items[0].body.service[0].id === infoJson) {
              currentPage.value = i + 1
            }
          })
        } else if (manifest.value.items.forEach) {
          manifest.value.items.forEach((item, i) => {
            if (item.items[0].items[0].body.service['@id'] === infoJson){
              currentPage.value = i + 1
            } else if (item.items[0].items[0].body.service[0].id === infoJson) {
              currentPage.value = i + 1
            }
          })
        }
      } else {
        manifest.value.sequences[0].canvases.forEach((canvas, i) => {
          if (canvas.images[0].resource.service['@id'] === infoJson) {
            currentPage.value = i + 1
          }
        })
      }   
    } catch (e) {
      console.log('', e)
    }
    if (!currentPage.value) currentPage.value = 1
  } else {
    currentPage.value = 1
  }
}

//イメージ一覧とペジネーション

const imageList = computed(() => {
  if (manifest.value) {
    if (iiifType.value === 'normal') {
      if (isV3.value) {
        if (manifest.value.items.every(i => i.thumbnail[0]?.id)) {
          return manifest.value.items.map(i => i.thumbnail[0]?.id)
        }
      } else {
        if (manifest.value.sequences[0].canvases.every(c => c.thumbnail)) {
          //サムネイルがManifestに定義してあればそれを返す
          return manifest.value.sequences[0].canvases.map(
            c => c.thumbnail['@id'],
          )
        } else {
          //マニフェストが無ければ幅200のImageAPIURLのリストにして返す
          return manifest.value.sequences[0].canvases
            .map(c => c.images[0].resource.service['@id'] + '/info.json')
            .map(ij => iiifImageUrlFromInfo(ij, null, { w: 200 }))
        }
      }
    } else if (iiifType.value === 'direct') {
      return manifest.value.sequences[0].canvases.map(c => c.thumbnail['@id'])
    }
  } else if (urls.value) {
    if (props.image.thumbnails) return props.image.thumbnails
    else return props.image.urls
  }
  return []
})

const thumbnailListModal = ref<InstanceType<typeof ThumbnailListModal>>()
const showList = async () => {
  try {
    currentPage.value = await thumbnailListModal.value.openAwait({
      imageList: imageList.value
    })
  } catch (cancel) {}
}

const inputPageModel: Ref<number> = ref(0)
const inputPageModeRefresh: Ref<string> = ref('')

const totalPage = computed(() => {
  if (manifest.value) {
    if (isV3.value) {
      return manifest.value.items.length
    } else {
      return manifest.value.sequences[0].canvases.length
    }
  } else if (urls.value) {
    return urls.value.length
  }
  return 0
})

const inputPage = (delay: number) => {
  debounce(delay, () => {
    if (inputPageModel.value == null) currentPage.value = 1
    else if (inputPageModel.value < 1) currentPage.value = 1
    else if (inputPageModel.value > totalPage.value)
      currentPage.value = totalPage.value
    else currentPage.value = inputPageModel.value
    inputPageModel.value = currentPage.value
    inputPageModeRefresh.value = `${inputPageModel.value}`
  })()
}

const next = () => {
  if (currentPage.value < totalPage.value) currentPage.value++
}

const previous = () => {
  if (currentPage.value > 1) currentPage.value--
}

watch(
  () => currentPage.value,
  (n, o, f) => {
    inputPageModel.value = n
    if (n > 0) {
      if (manifest.value) {
        if (iiifType.value === 'normal') {
          if (isV3.value) {
            const infoUrl =
              manifest.value.items[currentPage.value - 1].items[0].items[0].body
                .service[0].id + '/info.json'
            setInfo(infoUrl)
          } else {
            let service = null
            if (
              Array.isArray(
                manifest.value.sequences[0].canvases[currentPage.value - 1]
                  .images[0].resource.service,
              )
            ) {
              service =
                manifest.value.sequences[0].canvases[currentPage.value - 1]
                  .images[0].resource.service[0]
            } else {
              service =
                manifest.value.sequences[0].canvases[currentPage.value - 1]
                  .images[0].resource.service
            }
            const infoUrl = service['@id'] + '/info.json'
            setInfo(infoUrl)
          }
        } else {
          if (isV3.value) {
            setUrl(
              manifest.value.items[currentPage.value - 1].items[0].items[0].body
                .id,
            )
          } else {
            setUrl(
              manifest.value.sequences[0].canvases[currentPage.value - 1]
                .images[0].resource['@id'],
            )
          }
        }
      } else if (urls.value) {
        setUrl(urls.value[currentPage.value - 1])
      }
    }
  },
)

////////////////Annotations//////////

/////////////Rects////////////////

let leafletRectangles // L.Rectangle[] //vueの範囲外

const initRect = () => {
  if (props.rects && map) {
    if (leafletRectangles) {
      if (props.rects.length < leafletRectangles.length) selectRect(null)
      leafletRectangles.forEach(r => r.remove())
    }
    leafletRectangles = []
    props.rects.forEach((r, index) => {
      const lr: any = $createLeafletRectangle(map, infoUrl.value, r.x, r.y, r.w, r.h)
      lr.addTo(rectLayer)
      if (props.mode === 'edit-rect') {
        activateRect(lr)
        if (selectedIndex.value === index) selectRect(lr)
      } else if (props.mode === 'show-rect') {
        activateRectShow(lr)
        selectRect(null)
      }
    })
    setStyle()
  }
}

watch(() => props.rects, initRect, { immediate: true })

// const activateRectShow = (r: L.Rectangle) => {
const activateRectShow = (r) => {
  if (!leafletRectangles) leafletRectangles = []
  leafletRectangles.push(r)
  r.on('click', ev => {
    selectRect(r)
  })
}

// const activateRect = (r: L.Rectangle) => {
const activateRect = (r: any) => {
  r.enableEdit()
  if (!leafletRectangles) leafletRectangles = []
  leafletRectangles.push(r)
  r.on('click dragstart editable:vertex:dragstart', ev => {
    selectRect(r)
  })
  r.on('dragend editable:vertex:dragend', ev => {
    updateRects()
    selectRect(r)
  })
}

const selectedIndex: Ref<number> = ref(-1)

const setStyle = () => {
  leafletRectangles.forEach(lr => {
    lr.setStyle({ fillColor: null })
  })
  if (selectedIndex.value >= 0)
    leafletRectangles[selectedIndex.value].setStyle({
      fillColor: 'red',
    })
}

watch(
  () => selectedIndex.value,
  () => {
    setStyle()
    emits('select-rect', selectedIndex.value)
  },
)

// const selectRect = (r: L.Rectangle) => {
const selectRect = (r) => {
  selectedIndex.value = leafletRectangles.indexOf(r)
}

const updateRects = () => {
  const innerRect = leafletRectangles.map(leafletRect =>
    mapBoundLatLng2Point(leafletRect.getBounds(), props.ratio),
  )
  emits('update-rects', innerRect)
}

const addRect = () => {
  // const lr: L.Rectangle = map['editTools'].startRectangle.call(
  const lr = map['editTools'].startRectangle.call(
    map['editTools'],
  )
  try {
    // 座標(NorthWest)が取れないRectangleが生成される時があるので、その場合は追加しない
    lr.getBounds().getNorthWest()
    lr.getBounds().getSouthEast()
    activateRect(lr)
  } catch (e) {
    console.error('addRect error.', e)
  }
}

// function mapBoundLatLng2Point(bound: L.LatLngBounds, ratio?: number): Rect {
const mapBoundLatLng2Point = (bound, ratio?: number): Rect => {
  let tl = { x: 0, y:0 }
  let br = { x: 0, y:0 }
  try {
    // 座標が取れない場合は仕方なく0, 0で処理を進める
    tl = mapLatLng2Point(bound.getNorthWest())
    br = mapLatLng2Point(bound.getSouthEast())
  } catch (e) {
    console.error('mapBoundLatLng2Point error.', e)
  }
  const w = Math.floor(br.x - tl.x)
  const h = ratio ? w * ratio : Math.floor(br.y - tl.y)

  return {
    x: Math.floor(tl.x),
    y: Math.floor(tl.y),
    w,
    h,
  }
}

// function mapLatLng2Point(latlng: L.LatLng): L.Point {
const mapLatLng2Point = (latlng) => {
  return map.project(
    latlng,
    infoUrl.value ? map.getMaxZoom() : map.getMaxZoom() - 1,
  )
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

$iiif-viewer-background-color: $jps-gray-100;

.iiif-viewer {
  width: 100%;
  padding: 1rem 0;
  p {
    line-height: inherit;
  }

  .viewer-container {
    position: relative;
    margin: 1rem 0;
  }

  .viewer,
  .iiif-viewer-blocker {
    height: 50vh;
    margin: 0;

    @include is-mobile() {
      height: 56.25vw;
      /* 16:9 */
    }
    @include is-tablet {
      height: 50vh;
    }
  }

  .iiif-viewer-blocker {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    display: block;
    z-index: 1;
  }


  .leaflet-container {
    background-color: $iiif-viewer-background-color;
    margin: auto;
    border: 1px dashed $jps-gray-300;
    border-radius: $global-radius;
    z-index: 1;
    @include is-mobile() {
      max-width: 100%;
    }
    @include is-tablet() {
      max-width: $content-width;
    }
    @include is-desktop {
      max-width: $wide-content-width;
    }
  }

  .leaflet-fade-anim .leaflet-tile {
    will-change: initial;
  }

  .iiif-control-slider {
    padding: 0 3rem;

    .j-slider {
      margin-top: 0.5rem;
      margin-bottom: 0.5rem;
    }

    background-color: $iiif-viewer-background-color;

    @include is-mobile {
      padding: 0 2rem;
    }
  }

  .iiif-control-group {
    position: relative;
    width: 100%;
    display: flex;
    gap: 16px;
    align-items: center;
    justify-content: center;
    padding: 0rem 3rem 0.5rem 3rem;
    margin-top: 0.5rem;

    .control-primary,
    .control-secondery,
    .iiif-control-page,
    .iiif-annot,
    .iiif-control-zoom {
      display: flex;
      align-items: center;
      gap: 12px;

      @include is-mobile {
        gap: 8px;
      }
    }

    .iiif-control-page {
      gap: 0;
    }


    .jps-icon {
      margin: 0;
    }

    .iiif-control-separator {
      width: 1px;
      background-color: $border-grey;
      height: 24px;
    }

    @include is-mobile {
      padding: 0 0.5rem 0.5rem;
      flex-direction: column;

      .iiif-control-page {
        margin-left: 0.5rem;
      }

      .control-secondery {
        margin-top: 0.25rem;
      }
    }

    .label-button {
      background: none;
      border: none;
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      font-size: $size-6;
      color: $jps-gray-800;

      .jps-icon {
        color: $jps-gray-800;
      }

      @include is-mobile {
        height: 1.5rem;
      }
    }

    .iiif-control-page {
      justify-content: center;
      position: relative;

      .jps-iiif-list {
        position: absolute;
        left: 1rem;
      }

      .jps-iiif-inputpage {
        text-align: right;
        background-color: $white;
        border: 1px solid $jps-gray-600;
        width: 6rem;
        height: 2.5rem;
        border-radius: 0.375rem;
        padding: 1rem;
        margin: 0 0.5rem;

        @include is-mobile {
          height: 2rem;
        }
      }

      .jps-iiif-prev {}

      .jps-iiif-total {
        background: none;
        border: none;
        color: $white;
        margin-right: 0.5rem;

        .modal & {
          color: $black;
        }
      }
    }

    .iiif-control-zoom {
      i {
        margin-right: 0.5rem;
      }
    }

    .jps-iiif-api {
      height: 21px;

      .jps-iiif-manifest {
        img {
          height: 1.25rem;
        }
      }
    }

    @include is-IE() {
      .jps-iiif-full {
        display: none;
      }
    }
  }

  /* Full Screen用 */
  &.is-full {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100vh;
    /* TODO 現行と違ってフルスクリーンになったときにヘッダー部分が裏に入っている。また現行では裏の表示がかなり消えるようになっている */
    z-index: 900;

    .viewer {
      height: calc(100vh - 6rem);

      @include is-mobile {
        height: calc(100vh - 4.75rem);
      }
    }

    .iiif-control-slider {
      position: fixed;
      bottom: 4rem;
      z-index: 910;
      width: 100%;

      @include is-mobile {
        bottom: 3.5rem;
      }
    }

    .iiif-control-group {
      position: fixed;
      bottom: 0;
      z-index: 910;

      @include is-mobile {
        bottom: 1rem;
      }
    }

    .control-secondery {
      display: none;
    }
  }
}
</style>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.tooltip {
  display: block !important;
  z-index: 10000;

  .tooltip-inner {
    background: $jps-gray-800;
    color: $white;
    border-radius: 8px;
    padding: 2px 10px 4px;
  }

  .tooltip-arrow {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    border-color: $jps-gray-800;
    z-index: 1;
  }

  &[x-placement^="top"] {
    margin-bottom: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 0 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      bottom: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[aria-hidden='true'] {
    visibility: hidden;
    opacity: 0;
    transition: opacity .15s, visibility .15s;
  }

  &[aria-hidden='false'] {
    visibility: visible;
    opacity: 1;
    transition: opacity .15s;
  }
}
</style>
